import { useState, useCallback } from 'react';

import { addEquipment } from '../api/addEquipmentApiService';
import { getPresignedUrlsBatch } from '../api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '../api/uploadFileToS3';
import { EQUIPMENT_ERROR_MESSAGES } from '../errors';
import type {
  EquipmentEntry,
  EquipmentEntryErrors,
  MediaResource,
} from '../types/addEquipmentType';

const ERROR_MESSAGES = EQUIPMENT_ERROR_MESSAGES;

const EMOJI_REGEX = /\p{Extended_Pictographic}/u;

/** Validates a single entry and returns a partial errors object for the new fields. */
export const validateEntryFields = (
  entry: EquipmentEntry
): Partial<EquipmentEntryErrors> => {
  const categoryError = !entry.category ? ERROR_MESSAGES.CATEGORY_REQUIRED : '';

  let serialNumberError = '';
  if (entry.serialNumber) {
    if (entry.serialNumber.length > 200) {
      serialNumberError = ERROR_MESSAGES.SERIAL_NUMBER_TOO_LONG;
    } else if (EMOJI_REGEX.test(entry.serialNumber)) {
      serialNumberError = ERROR_MESSAGES.SERIAL_NUMBER_INVALID_CHARS;
    }
  }

  let dateOfProductionError = '';
  if (entry.dateOfProduction) {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (entry.dateOfProduction > today) {
      dateOfProductionError = ERROR_MESSAGES.DATE_FUTURE;
    }
  }

  let modelNameError = '';
  if (entry.modelName) {
    if (entry.modelName.length > 200) {
      modelNameError = ERROR_MESSAGES.MODEL_NAME_TOO_LONG;
    } else if (EMOJI_REGEX.test(entry.modelName)) {
      modelNameError = ERROR_MESSAGES.MODEL_NAME_INVALID_CHARS;
    }
  }

  return {
    category: categoryError,
    serialNumber: serialNumberError,
    dateOfProduction: dateOfProductionError,
    modelName: modelNameError,
  };
};

interface EquipmentItem {
  entry: EquipmentEntry;
  errors: EquipmentEntryErrors;
  uploadProgress: Record<string, number>;
  failedUploadFiles: string[];
}

const createEmptyEntry = (): EquipmentEntry => ({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  category: '',
  serialNumber: '',
  dateOfProduction: undefined,
  modelName: '',
  docFiles: [],
  docResources: {},
});

const createEmptyErrors = (): EquipmentEntryErrors => ({
  name: '',
  description: '',
  category: '',
  serialNumber: '',
  dateOfProduction: '',
  modelName: '',
  imageFile: '',
  docFiles: '',
});

const createEmptyItem = (): EquipmentItem => ({
  entry: createEmptyEntry(),
  errors: createEmptyErrors(),
  uploadProgress: {},
  failedUploadFiles: [],
});

export interface UseAddEquipmentReturn {
  equipmentList: EquipmentEntry[];
  errorsList: EquipmentEntryErrors[];
  uploadProgressList: Record<string, number>[];
  failedUploadFilesList: string[][];
  isSubmitting: boolean;
  addEntry: () => void;
  removeEntry: (id: string) => void;
  updateField: (
    id: string,
    field: keyof Pick<
      EquipmentEntry,
      'name' | 'description' | 'serialNumber' | 'modelName'
    >,
    value: string
  ) => void;
  updateCategory: (id: string, value: string) => void;
  updateDateOfProduction: (id: string, date: Date | undefined) => void;
  handleImageChange: (id: string, files: File[]) => void;
  handleImageDelete: (id: string) => void;
  handleImageError: (id: string, message: string) => void;
  handleDocsChange: (id: string, files: File[]) => void;
  handleDocsError: (id: string, message: string) => void;
  clearEntryErrors: (id: string) => void;
  setEntrySerialError: (id: string, message: string) => void;
  validateAll: () => boolean;
  validateEntry: (id: string) => boolean;
  submitEntry: (
    id: string,
    facilityId: string,
    clientId: string
  ) => Promise<{ success: boolean; error?: string; equipmentId?: string }>;
  submit: (facilityId: string, clientId: string) => Promise<void>;
  retryUpload: (entryId: string, filename: string) => Promise<void>;
}

export const useAddEquipment = (): UseAddEquipmentReturn => {
  const [items, setItems] = useState<EquipmentItem[]>([createEmptyItem()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const equipmentList = items.map((item) => item.entry);
  const errorsList = items.map((item) => item.errors);
  const uploadProgressList = items.map((item) => item.uploadProgress);
  const failedUploadFilesList = items.map((item) => item.failedUploadFiles);

  const addEntry = useCallback(() => {
    setItems((prev) => [...prev, createEmptyItem()]);
  }, []);

  const removeEntry = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.entry.id !== id));
  }, []);

  const updateField = useCallback(
    (
      id: string,
      field: keyof Pick<
        EquipmentEntry,
        'name' | 'description' | 'serialNumber' | 'modelName'
      >,
      value: string
    ) => {
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === id
            ? {
                ...item,
                entry: { ...item.entry, [field]: value },
                errors: { ...item.errors, [field]: '' },
              }
            : item
        )
      );
    },
    []
  );

  const updateCategory = useCallback((id: string, value: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === id
          ? {
              ...item,
              entry: { ...item.entry, category: value },
              errors: { ...item.errors, category: '' },
            }
          : item
      )
    );
  }, []);

  const updateDateOfProduction = useCallback(
    (id: string, date: Date | undefined) => {
      const utcDate = date
        ? new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
          )
        : undefined;
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === id
            ? {
                ...item,
                entry: { ...item.entry, dateOfProduction: utcDate },
                errors: { ...item.errors, dateOfProduction: '' },
              }
            : item
        )
      );
    },
    []
  );

  const uploadFile = useCallback(
    async (
      entryId: string,
      file: File,
      pathTemplate: string,
      slot: 'image' | 'docs'
    ): Promise<MediaResource> => {
      const key = `${slot}:${file.name}`;
      // Mark as uploading
      setItems((prev) =>
        prev.map((item) =>
          item.entry.id === entryId
            ? {
                ...item,
                uploadProgress: { ...item.uploadProgress, [key]: 0 },
                failedUploadFiles: item.failedUploadFiles.filter(
                  (f) => f !== key
                ),
              }
            : item
        )
      );

      try {
        const presignedResponse = await getPresignedUrlsBatch({
          paths: [pathTemplate],
          generatePath: true,
        });

        const fileData = presignedResponse.files[0];
        if (!fileData) {
          throw new Error(ERROR_MESSAGES.DEFAULT_ERROR);
        }

        await uploadFileToS3(fileData.url, file, (percent) => {
          setItems((prev) =>
            prev.map((item) =>
              item.entry.id === entryId
                ? {
                    ...item,
                    uploadProgress: {
                      ...item.uploadProgress,
                      [key]: percent,
                    },
                  }
                : item
            )
          );
        });

        const resource: MediaResource = {
          path: fileData.path,
          resourceId: fileData.resourceId,
          isUploaded: true,
          filename: file.name,
          fileType: file.type,
          fileSize: file.size,
        };

        // Clear upload progress on success
        setItems((prev) =>
          prev.map((item) => {
            if (item.entry.id !== entryId) {
              return item;
            }
            const { [key]: _removed, ...rest } = item.uploadProgress;
            return { ...item, uploadProgress: rest };
          })
        );

        return resource;
      } catch {
        // Mark as failed
        setItems((prev) =>
          prev.map((item) => {
            if (item.entry.id !== entryId) {
              return item;
            }
            const { [key]: _removed, ...rest } = item.uploadProgress;
            return {
              ...item,
              uploadProgress: rest,
              failedUploadFiles: [
                ...item.failedUploadFiles.filter((f) => f !== key),
                key,
              ],
            };
          })
        );
        throw new Error(ERROR_MESSAGES.DEFAULT_ERROR);
      }
    },
    []
  );

  const handleImageChange = useCallback(
    (id: string, files: File[]) => {
      const file = files[0];

      // Store file immediately for display, clear any old resource
      setItems((prev) =>
        prev.map((item): EquipmentItem => {
          if (item.entry.id !== id) {
            return item;
          }
          const entry: EquipmentEntry = { ...item.entry };
          if (file !== undefined) {
            entry.imageFile = file;
          } else {
            delete entry.imageFile;
          }
          delete entry.imageResource;
          return {
            ...item,
            entry,
            errors: { ...item.errors, imageFile: '' },
          };
        })
      );

      if (!file) {
        return;
      }

      // Upload immediately
      void uploadFile(
        id,
        file,
        `equipments/entityid-placeholder/image/resourceid-placeholder/${file.name}`,
        'image'
      )
        .then((resource) => {
          setItems((prev) =>
            prev.map((item) =>
              item.entry.id === id
                ? { ...item, entry: { ...item.entry, imageResource: resource } }
                : item
            )
          );
        })
        .catch(() => {
          // failure state already set inside uploadFile
        });
    },
    [uploadFile]
  );

  const handleImageDelete = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((item): EquipmentItem => {
        if (item.entry.id !== id) {
          return item;
        }
        const entry: EquipmentEntry = { ...item.entry };
        delete entry.imageFile;
        delete entry.imageResource;
        return { ...item, entry };
      })
    );
  }, []);

  const handleImageError = useCallback((id: string, message: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === id
          ? { ...item, errors: { ...item.errors, imageFile: message } }
          : item
      )
    );
  }, []);

  const handleDocsChange = useCallback(
    (id: string, files: File[]) => {
      const capped = files.slice(0, 2);
      const item = items.find((i) => i.entry.id === id);
      if (!item) {
        return;
      }

      const existingResources = item.entry.docResources;
      const newFiles = capped.filter((f) => !existingResources[f.name]);

      // Keep resources only for files still in the list
      const keptResources: Record<string, MediaResource> = {};
      for (const f of capped) {
        const res = existingResources[f.name];
        if (res) {
          keptResources[f.name] = res;
        }
      }

      setItems((prev) =>
        prev.map((i) =>
          i.entry.id === id
            ? {
                ...i,
                entry: {
                  ...i.entry,
                  docFiles: capped,
                  docResources: keptResources,
                },
                errors: { ...i.errors, docFiles: '' },
              }
            : i
        )
      );

      // Upload new files immediately
      for (const file of newFiles) {
        void uploadFile(
          id,
          file,
          `equipments/entityid-placeholder/docs/resourceid-placeholder/${file.name}`,
          'docs'
        )
          .then((resource) => {
            setItems((prev) =>
              prev.map((i) =>
                i.entry.id === id
                  ? {
                      ...i,
                      entry: {
                        ...i.entry,
                        docResources: {
                          ...i.entry.docResources,
                          [file.name]: resource,
                        },
                      },
                    }
                  : i
              )
            );
          })
          .catch(() => {
            // failure state already set inside uploadFile
          });
      }
    },
    [items, uploadFile]
  );

  const clearEntryErrors = useCallback((id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === id ? { ...item, errors: createEmptyErrors() } : item
      )
    );
  }, []);

  const setEntrySerialError = useCallback((id: string, message: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === id
          ? { ...item, errors: { ...item.errors, serialNumber: message } }
          : item
      )
    );
  }, []);

  const handleDocsError = useCallback((id: string, message: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.entry.id === id
          ? { ...item, errors: { ...item.errors, docFiles: message } }
          : item
      )
    );
  }, []);

  const validateAll = useCallback((): boolean => {
    let hasUploadInProgress = false;
    let hasUploadFailed = false;

    const validatedItems = items.map((item) => {
      if (Object.keys(item.uploadProgress).length > 0) {
        hasUploadInProgress = true;
      }
      if (item.failedUploadFiles.length > 0) {
        hasUploadFailed = true;
      }

      const nameError = !item.entry.name.trim() ? ERROR_MESSAGES.REQUIRED : '';
      const descriptionError = !item.entry.description.trim()
        ? ERROR_MESSAGES.REQUIRED
        : item.entry.description.length > 500
          ? ERROR_MESSAGES.DESCRIPTION_TOO_LONG
          : '';
      const imageFileError = !item.entry.imageFile
        ? ERROR_MESSAGES.REQUIRED
        : !item.entry.imageResource
          ? ERROR_MESSAGES.UPLOAD_INCOMPLETE
          : '';
      const newFieldErrors = validateEntryFields(item.entry);

      return {
        ...item,
        errors: {
          ...item.errors,
          name: nameError,
          description: descriptionError,
          imageFile: imageFileError,
          ...newFieldErrors,
        },
      };
    });

    setItems(validatedItems);

    if (hasUploadInProgress || hasUploadFailed) {
      return false;
    }

    return !validatedItems.some(
      (item) =>
        item.errors.name ||
        item.errors.description ||
        item.errors.category ||
        item.errors.serialNumber ||
        item.errors.dateOfProduction ||
        item.errors.modelName ||
        item.errors.imageFile
    );
  }, [items]);

  const validateEntry = useCallback(
    (id: string): boolean => {
      const item = items.find((i) => i.entry.id === id);
      if (!item) {
        return false;
      }

      const nameError = !item.entry.name.trim() ? ERROR_MESSAGES.REQUIRED : '';
      const descriptionError = !item.entry.description.trim()
        ? ERROR_MESSAGES.REQUIRED
        : item.entry.description.length > 500
          ? ERROR_MESSAGES.DESCRIPTION_TOO_LONG
          : '';
      const imageFileError = !item.entry.imageFile
        ? ERROR_MESSAGES.REQUIRED
        : !item.entry.imageResource
          ? ERROR_MESSAGES.UPLOAD_INCOMPLETE
          : '';
      const newFieldErrors = validateEntryFields(item.entry);

      setItems((prev) =>
        prev.map((i) =>
          i.entry.id === id
            ? {
                ...i,
                errors: {
                  ...i.errors,
                  name: nameError,
                  description: descriptionError,
                  imageFile: imageFileError,
                  ...newFieldErrors,
                },
              }
            : i
        )
      );

      return (
        !nameError &&
        !descriptionError &&
        !newFieldErrors.category &&
        !newFieldErrors.serialNumber &&
        !newFieldErrors.dateOfProduction &&
        !newFieldErrors.modelName &&
        !imageFileError &&
        Object.keys(item.uploadProgress).length === 0 &&
        item.failedUploadFiles.length === 0
      );
    },
    [items]
  );

  const submitEntry = useCallback(
    async (
      id: string,
      facilityId: string,
      clientId: string
    ): Promise<{ success: boolean; error?: string; equipmentId?: string }> => {
      const item = items.find((i) => i.entry.id === id);
      if (!item) {
        return { success: false };
      }

      const nameError = !item.entry.name.trim() ? ERROR_MESSAGES.REQUIRED : '';
      const descriptionError = !item.entry.description.trim()
        ? ERROR_MESSAGES.REQUIRED
        : item.entry.description.length > 500
          ? ERROR_MESSAGES.DESCRIPTION_TOO_LONG
          : '';
      const imageFileError = !item.entry.imageFile
        ? ERROR_MESSAGES.REQUIRED
        : !item.entry.imageResource
          ? ERROR_MESSAGES.UPLOAD_INCOMPLETE
          : '';
      const newFieldErrors = validateEntryFields(item.entry);

      setItems((prev) =>
        prev.map((i) =>
          i.entry.id === id
            ? {
                ...i,
                errors: {
                  ...i.errors,
                  name: nameError,
                  description: descriptionError,
                  imageFile: imageFileError,
                  ...newFieldErrors,
                },
              }
            : i
        )
      );

      if (
        nameError ||
        descriptionError ||
        newFieldErrors.category ||
        newFieldErrors.serialNumber ||
        newFieldErrors.dateOfProduction ||
        newFieldErrors.modelName ||
        imageFileError ||
        Object.keys(item.uploadProgress).length > 0 ||
        item.failedUploadFiles.length > 0
      ) {
        return { success: false };
      }

      const trimmedSerial = item.entry.serialNumber?.trim();
      if (
        trimmedSerial &&
        items.some(
          (i) =>
            i.entry.id !== id && i.entry.serialNumber?.trim() === trimmedSerial
        )
      ) {
        setItems((prev) =>
          prev.map((i) =>
            i.entry.id === id
              ? {
                  ...i,
                  errors: {
                    ...i.errors,
                    serialNumber: ERROR_MESSAGES.SERIAL_NUMBER_DUPLICATE,
                  },
                }
              : i
          )
        );
        return { success: false };
      }

      setIsSubmitting(true);
      try {
        const entry = item.entry;
        const docs: MediaResource[] | undefined =
          entry.docFiles.length > 0
            ? entry.docFiles
                .map((f) => entry.docResources[f.name])
                .filter((r): r is MediaResource => r !== undefined)
            : undefined;

        const dateOfProduction = entry.dateOfProduction
          ? entry.dateOfProduction.toISOString().split('T')[0]
          : undefined;

        const result = await addEquipment(
          facilityId,
          clientId,
          undefined,
          entry.name,
          entry.description,
          entry.category,
          entry.serialNumber || undefined,
          dateOfProduction,
          entry.modelName || undefined,
          entry.imageResource,
          docs
        );

        if (result.fieldErrors) {
          setItems((prev) =>
            prev.map((i) =>
              i.entry.id === id
                ? {
                    ...i,
                    errors: {
                      ...i.errors,
                      serialNumber: result.fieldErrors?.serialNumber ?? '',
                    },
                  }
                : i
            )
          );
          return { success: false };
        }

        return result.error
          ? { success: result.success, error: result.error }
          : {
              success: result.success,
              ...(result.equipmentId
                ? { equipmentId: result.equipmentId }
                : {}),
            };
      } finally {
        setIsSubmitting(false);
      }
    },
    [items]
  );

  const submit = useCallback(
    async (facilityId: string, clientId: string): Promise<void> => {
      if (!validateAll()) {
        return;
      }

      setIsSubmitting(true);
      try {
        for (const item of items) {
          const entry = item.entry;

          // Build doc resources in file order
          const docs: MediaResource[] | undefined =
            entry.docFiles.length > 0
              ? entry.docFiles
                  .map((f) => entry.docResources[f.name])
                  .filter((r): r is MediaResource => r !== undefined)
              : undefined;

          const dateOfProduction = entry.dateOfProduction
            ? entry.dateOfProduction.toISOString().split('T')[0]
            : undefined;

          const result = await addEquipment(
            facilityId,
            clientId,
            undefined,
            entry.name,
            entry.description,
            entry.category,
            entry.serialNumber || undefined,
            dateOfProduction,
            entry.modelName || undefined,
            entry.imageResource,
            docs
          );

          if (!result.success) {
            throw new Error(result.error || ERROR_MESSAGES.DEFAULT_ERROR);
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [items, validateAll]
  );

  const retryUpload = useCallback(
    async (entryId: string, filename: string): Promise<void> => {
      const item = items.find((i) => i.entry.id === entryId);
      if (!item) {
        return;
      }

      const isImage = filename.startsWith('image:');
      const actualFilename = isImage ? filename.slice(6) : filename.slice(5);
      const file = isImage
        ? item.entry.imageFile
        : item.entry.docFiles.find((f) => f.name === actualFilename);
      if (!file) {
        return;
      }

      const slot: 'image' | 'docs' = isImage ? 'image' : 'docs';
      const pathTemplate = isImage
        ? `equipments/entityid-placeholder/image/resourceid-placeholder/${actualFilename}`
        : `equipments/entityid-placeholder/docs/resourceid-placeholder/${actualFilename}`;

      try {
        const resource = await uploadFile(entryId, file, pathTemplate, slot);

        setItems((prev) =>
          prev.map((i) => {
            if (i.entry.id !== entryId) {
              return i;
            }
            if (isImage) {
              return { ...i, entry: { ...i.entry, imageResource: resource } };
            }
            return {
              ...i,
              entry: {
                ...i.entry,
                docResources: {
                  ...i.entry.docResources,
                  [actualFilename]: resource,
                },
              },
            };
          })
        );
      } catch {
        // uploadFile already sets failedUploadFiles in state
      }
    },
    [items, uploadFile]
  );

  return {
    equipmentList,
    errorsList,
    uploadProgressList,
    failedUploadFilesList,
    isSubmitting,
    addEntry,
    removeEntry,
    updateField,
    updateCategory,
    updateDateOfProduction,
    handleImageChange,
    handleImageDelete,
    handleImageError,
    handleDocsChange,
    handleDocsError,
    clearEntryErrors,
    setEntrySerialError,
    validateAll,
    validateEntry,
    submitEntry,
    submit,
    retryUpload,
  };
};
